// @ts-strict-ignore
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem } from '@node-space/ui-components'
import type { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import type { FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import { BeneficiaryDetailsCurrency, PaymentMethods } from 'types/beneficiaries'

type FiatInstrumentConfirmationDataListItemsProps = {
  formFieldLabels: FormFieldLabels
  formValues: ContactFiatInstrumentFormData
  beneficiaryAddress?: string
  bankAddress?: string
  intermediaryBankAddress?: string
  isAccountTypeRequired: boolean
  isSWIFT: boolean
}

export const FiatInstrumentConfirmationDataListItems = ({
  formFieldLabels,
  formValues,
  beneficiaryAddress,
  bankAddress,
  intermediaryBankAddress,
  isAccountTypeRequired,
  isSWIFT,
}: FiatInstrumentConfirmationDataListItemsProps) => {
  const { t } = useTranslation()

  const { currency, bankCode, swiftCode, paymentMethods, bankCountryCode } = formValues ?? {}
  const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)
  
  const showBankCode = useMemo(() => {
    switch (currency) {
      case BeneficiaryDetailsCurrency.USD: {
        const isUSBank = bankCountryCode === 'US'
        return (!isSwift && !!bankCode) || (isSwift && !!bankCode && isUSBank)
      }
      case BeneficiaryDetailsCurrency.EUR:
        return !isSwift
      case BeneficiaryDetailsCurrency.GBP: {
        const isFasterPayment = paymentMethods?.includes(PaymentMethods.FASTER_PAYMENT)
        return !!bankCode && ((isSwift && isFasterPayment) || !isSwift)
      }
      default:
        return false
    }
  }, [bankCode, currency, isSwift, bankCountryCode, paymentMethods])

  const showSwiftCode = useMemo(() => {
    switch (currency) {
      case BeneficiaryDetailsCurrency.EUR: {
        const isLocal =
          (paymentMethods?.includes(PaymentMethods.SEPA) ||
            paymentMethods?.includes(PaymentMethods.SEPA_INSTANT)) &&
          !paymentMethods?.includes(PaymentMethods.SWIFT)
        return isSwift && !isLocal && !!swiftCode
      }
      case BeneficiaryDetailsCurrency.USD:
      case BeneficiaryDetailsCurrency.GBP:
      default:
        return !!swiftCode && isSwift
    }
  }, [swiftCode, currency, isSwift, paymentMethods])

  return (
    <>
      {/* Beneficiary details */}
      <Box>
        <DataListItem
          size="large"
          id="currency"
          title={t('formFields.currency')}
          body={formValues?.currency}
          layout="horizontal"
        />
        <DataListItem
          size="large"
          id="description"
          title={t('formFields.beneficiaryDescription')}
          body={formValues?.description}
          layout="horizontal"
        />

        {formValues?.firstName && (
          <DataListItem
            size="large"
            id="firstName"
            title={t('firstName')}
            body={formValues?.firstName}
            layout="horizontal"
          />
        )}
        {formValues?.lastName && (
          <DataListItem
            size="large"
            id="lastName"
            title={t('lastName')}
            body={formValues?.lastName}
            layout="horizontal"
          />
        )}
        {formValues?.businessName && (
          <DataListItem
            size="large"
            id="businessName"
            title={t('businessName')}
            body={formValues?.businessName}
            layout="horizontal"
          />
        )}
        {formValues?.paymentReference && (
          <DataListItem
            size="large"
            id="paymentReference"
            title={t('beneficiaryPaymentReference')}
            body={formValues?.paymentReference}
            layout="horizontal"
          />
        )}
        {beneficiaryAddress && (
          <DataListItem
            size="large"
            id="beneficiaryAddress"
            title={t('beneficiaryAddress')}
            body={beneficiaryAddress}
            layout="horizontal"
          />
        )}
      </Box>

      {/* Bank Details */}
      <Box>
        <DataListItem
          size="large"
          id="accountNumber"
          title={formFieldLabels?.accountNumber}
          body={formValues?.accountNumber}
          layout="horizontal"
        />
        {showBankCode && <DataListItem
          size="large"
          id="bankCode"
          title={formFieldLabels?.bankCode}
          body={formValues?.bankCode}
          layout="horizontal"
        />}
        {showSwiftCode && <DataListItem
          size="large"
          id="swiftCode"
          title={t('bicSwiftCode')}
          body={formValues?.swiftCode}
          layout="horizontal"
        />}
        {isAccountTypeRequired && formValues?.accountType && (
          <DataListItem
            size="large"
            id="accountType"
            title={t('formFields.accountType')}
            body={formValues?.accountType}
            layout="horizontal"
          />
        )}
        {formValues?.bankName && (
          <DataListItem
            size="large"
            id="bankCode"
            title={t('bankName')}
            body={formValues?.bankName}
            layout="horizontal"
          />
        )}
        {bankAddress && (
          <DataListItem
            size="large"
            id="bankAddress"
            title={t('bankAddress')}
            body={bankAddress}
            layout="horizontal"
          />
        )}
      </Box>

      {/* IntermediaryBankDetails */}
      {isSWIFT && formValues?.isIntermediaryBankDetailsRequired && (
        <Box paddingT={20}>
          <h2 className="text-foreground-prominent text-h2">
            {t('manageBeneficiaries.intermediaryBankDetails')}
          </h2>
          <DataListItem
            size="large"
            id="intermediaryBankName"
            title={t('manageBeneficiaries.intermediarysBankName')}
            body={formValues?.intermediaryBankName}
            layout="horizontal"
          />
          {formValues?.intermediaryBankSwiftCode && (
            <DataListItem
              size="large"
              id="swiftCode"
              title={t('bicSwiftCode')}
              body={formValues?.intermediaryBankSwiftCode}
              layout="horizontal"
            />
          )}
           {formValues?.intermediaryBankCode && (
            <DataListItem
              size="large"
              id="routingNumber"
              title={t('formFields.routingNumber')}
              body={formValues?.intermediaryBankCode}
              layout="horizontal"
            />
          )}
          {intermediaryBankAddress && (
            <DataListItem
              size="large"
              id="bankAddress"
              title={t('intermediaryBankAddress')}
              body={intermediaryBankAddress}
              layout="horizontal"
            />
          )}
        </Box>
      )}
    </>
  )
}

